import { formatCount } from "../lib/metrics";

/**
 * A self-contained inline-SVG area chart (no chart library). Draws a filled
 * gradient area under an accent line with a glowing end-point. A flat/empty
 * series renders a baseline so the card never looks broken. Shared by the owner
 * Command Center and the user Analytics page so both speak one visual language.
 */
export function MiniChart({ points }: { points: number[] }) {
  const W = 240;
  const H = 48;
  const PAD = 3;
  const max = Math.max(1, ...points);
  const n = points.length;
  const x = (i: number) => (n <= 1 ? 0 : (i / (n - 1)) * (W - PAD * 2) + PAD);
  const y = (v: number) => H - PAD - (v / max) * (H - PAD * 2);
  const line = points
    .map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`)
    .join(" ");
  const area = `${line} L${x(n - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  const lastX = x(n - 1);
  const lastY = y(points[n - 1] ?? 0);
  const id = `spark-${points.length}-${max}`;

  return (
    <svg className="ops-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.35" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <path
        d={line}
        fill="none"
        stroke="var(--accent)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      <circle cx={lastX} cy={lastY} r="2.4" fill="var(--accent-bright)" />
    </svg>
  );
}

/**
 * One trend metric: label, its window total, last-7d-vs-prior-7d delta, and a
 * sparkline. `points` are one value per day (oldest → newest). `windowLabel`
 * names the total's window ("30-day total"). `format` renders the numbers.
 */
export function TrendCard({
  label,
  points,
  windowLabel = "30-day total",
  format = formatCount,
}: {
  label: string;
  points: number[];
  windowLabel?: string;
  format?: (n: number) => string;
}) {
  const total = points.reduce((a, b) => a + b, 0);
  const last7 = points.slice(-7).reduce((a, b) => a + b, 0);
  const prev7 = points.slice(-14, -7).reduce((a, b) => a + b, 0);
  const delta = last7 - prev7;
  return (
    <div className="ops-chartcard">
      <div className="ops-chartcard-head">
        <span className="ops-chartcard-label">{label}</span>
        <span className="ops-chartcard-total">{format(total)}</span>
      </div>
      <MiniChart points={points} />
      <div className="ops-chartcard-foot">
        <span className="muted small">{windowLabel}</span>
        {prev7 > 0 || last7 > 0 ? (
          <span className={`ops-delta ${delta > 0 ? "up" : delta < 0 ? "down" : ""}`}>
            {delta > 0 ? "▲" : delta < 0 ? "▼" : "•"} {format(Math.abs(delta))} vs prev 7d
          </span>
        ) : null}
      </div>
    </div>
  );
}
